import { useEffect, useState } from 'react'
import { Loader2, ShieldOff, MoreHorizontal } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { api } from '@/lib/api'
import { AdminMfaResetDialog } from '@/components/admin/AdminMfaResetDialog'

interface AdminUser {
  id: string
  email: string
  fullName: string | null
  organizationId: string | null
  organizationName: string | null
  role: 'admin' | 'member' | 'viewer'
  mfaEnrolledAt: string | null
  mfaRequired: boolean
  isPlatformAdmin: boolean
  lastActiveAt: string | null
}

function formatDate(s: string | null): string {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatRelative(s: string | null): string {
  if (!s) return 'Never'
  const date = new Date(s)
  const diffDays = Math.floor((Date.now() - date.getTime()) / 86_400_000)
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
  return formatDate(s)
}

export function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null)

  const load = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await api.get<{ users: AdminUser[] }>('/admin/users')
      setUsers(data.users)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const handleReset = (user: AdminUser) => {
    setResetTarget(user)
  }

  const handleResetConfirmed = () => {
    setResetTarget(null)
    toast.success('User MFA reset')
    void load()
  }

  return (
    <div className="px-4 py-3 space-y-4">
      <div>
        <h1 className="text-sm font-medium">Users</h1>
        <p className="text-meta mt-1">
          All users across all organisations. Reset MFA only after verifying identity out-of-band.
        </p>
      </div>

      {error && (
        <div className="text-sm text-destructive">{error}</div>
      )}

      <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left text-meta">
              <th className="p-2 font-medium">Email</th>
              <th className="p-2 font-medium">Name</th>
              <th className="p-2 font-medium">Organisation</th>
              <th className="p-2 font-medium">Role</th>
              <th className="p-2 font-medium">MFA</th>
              <th className="p-2 font-medium">Last active</th>
              <th className="p-2 font-medium w-12"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="p-6 text-center text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                  Loading...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-6 text-center text-muted-foreground">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="border-t">
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <span>{u.email}</span>
                      {u.isPlatformAdmin && (
                        <Badge variant="outline" className="text-xs">Platform Admin</Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-2 text-muted-foreground">{u.fullName ?? '—'}</td>
                  <td className="p-2 text-muted-foreground">{u.organizationName ?? '—'}</td>
                  <td className="p-2 text-muted-foreground capitalize">{u.role}</td>
                  <td className="p-2">
                    {u.mfaEnrolledAt ? (
                      <Badge variant="success">
                        Enabled · {formatDate(u.mfaEnrolledAt)}
                      </Badge>
                    ) : (
                      <Badge variant="outline">Not enabled</Badge>
                    )}
                  </td>
                  <td className="p-2 text-muted-foreground">{formatRelative(u.lastActiveAt)}</td>
                  <td className="p-2">
                    <TooltipProvider>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" aria-label="Actions">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {u.mfaEnrolledAt ? (
                            <DropdownMenuItem onClick={() => handleReset(u)}>
                              <ShieldOff className="h-4 w-4 mr-2" />
                              Reset MFA
                            </DropdownMenuItem>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <DropdownMenuItem disabled>
                                    <ShieldOff className="h-4 w-4 mr-2" />
                                    Reset MFA
                                  </DropdownMenuItem>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>Not enrolled</TooltipContent>
                            </Tooltip>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TooltipProvider>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AdminMfaResetDialog
        open={resetTarget !== null}
        onOpenChange={(open) => !open && setResetTarget(null)}
        user={resetTarget ? { id: resetTarget.id, email: resetTarget.email } : null}
        onReset={handleResetConfirmed}
      />
    </div>
  )
}
